/**
 * Password complexity rules — must mirror
 * `api/app/schemas/validation.py::validate_password_complexity`.
 * If a rule changes here, change it there too (and the FAQ copy).
 *
 * `validatePassword` returns null on success or a single human-
 * readable error string listing every missing class, so the user
 * fixes everything at once instead of one failure at a time. Length
 * + complexity live in the same helper for the same reason — the
 * backend collapses them into one message too.
 *
 * `passwordChecklist` returns the per-rule pass/fail snapshot the
 * forms render as a live "rules satisfied" UI under the input —
 * the rules-as-you-type cue Brett asked for.
 */
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;

export interface PasswordRuleStatus {
  label: string;
  ok: boolean;
}

export function passwordChecklist(value: string): PasswordRuleStatus[] {
  return [
    {
      label: `At least ${PASSWORD_MIN_LENGTH} characters`,
      ok: value.length >= PASSWORD_MIN_LENGTH,
    },
    { label: 'An uppercase letter', ok: /[A-Z]/.test(value) },
    { label: 'A lowercase letter', ok: /[a-z]/.test(value) },
    { label: 'A digit', ok: /\d/.test(value) },
    {
      label: 'A special character',
      // Anything that isn't a letter, digit, or whitespace. Matches
      // the backend's `not c.isalnum() and not c.isspace()`.
      ok: /[^A-Za-z0-9\s]/.test(value),
    },
  ];
}

export function validatePassword(value: string): string | null {
  const missing = passwordChecklist(value)
    .filter((rule) => !rule.ok)
    .map((rule) => rule.label.toLowerCase());
  if (missing.length === 0) {
    return null;
  }
  return `Password must contain ${missing.join(', ')}.`;
}
